/**
 * Decodes Excel/XML escape sequences in a string (e.g., _x0020_ -> space)
 * Excel uses _xHHHH_ format where HHHH is a 4-digit hex Unicode code point
 * @param {string} text - Text that may contain Excel XML escape sequences
 * @returns {string} - Decoded text
 */
export const decodeExcelXmlEscapes = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Match _xHHHH_ pattern where HHHH is a 4-digit hex code
  // Use case-insensitive flag for the 'x' as well (some sources use _X0020_)
  return text.replace(/_[xX]([0-9A-Fa-f]{4})_/g, (match, hex) => {
    return String.fromCharCode(parseInt(hex, 16));
  });
};

/**
 * Decodes HTML entities in a string (e.g., &rsquo; -> ', &amp; -> &)
 * @param {string} text - Text that may contain HTML entities
 * @returns {string} - Decoded text
 */
export const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Use a textarea element to decode HTML entities
  // This is the most reliable browser-native approach
  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  // Fallback for SSR: handle common entities manually
  const entities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&rsquo;': "'",
    '&lsquo;': "'",
    '&rdquo;': '"',
    '&ldquo;': '"',
    '&ndash;': '–',
    '&mdash;': '—',
    '&nbsp;': ' ',
  };

  return text.replace(/&[^;]+;/g, (entity) => entities[entity] || entity);
};
